import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { authSelector } from '../features/auth/slice';
import { FileText, CheckCircle2, AlertCircle, Clock, ChevronLeft, ChevronRight, Loader2, Undo2, CheckCheck, XCircle } from 'lucide-react';

function fmt(n) {
  return Number(n || 0).toLocaleString();
}

function fmtDate(str) {
  if (!str) return '-';
  const d = new Date(str);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function getMonthOptions() {
  const now = new Date();
  const opts = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = `${d.getFullYear()}년 ${d.getMonth() + 1}월`;
    opts.push({ value: val, label });
  }
  return opts;
}

export default function TaxInvoicePage() {
  const { me } = useSelector(authSelector.selectAuthState);
  const navigate = useNavigate();
  useEffect(() => {
    if (me && me.superAdminYn !== 'Y') navigate('/dashboard', { replace: true });
  }, [me]); // eslint-disable-line react-hooks/exhaustive-deps

  const monthOptions = getMonthOptions();
  const [month, setMonth] = useState(monthOptions[1]?.value || monthOptions[0]?.value);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [carryingOver, setCarryingOver] = useState(false);
  const [confirmingId, setConfirmingId] = useState(null);
  const [tab, setTab] = useState('notIssued');

  const fetchStatus = useCallback(async (m) => {
    setLoading(true);
    try {
      const { data: res } = await axios.get('/api/tax-invoice/status', { params: { calculateMonth: m } });
      setData(res);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { if (month) fetchStatus(month); }, [month, fetchStatus]);

  const handleConfirm = async (clientId, clientName) => {
    if (!window.confirm(`${clientName} — 세금계산서 수신확인 처리하시겠습니까?\n(홈택스에서 해당 세금계산서를 확인한 경우에만 클릭)`)) return;
    setConfirmingId(clientId);
    try {
      await axios.post('/api/tax-invoice/manual-confirm', { clientId, calculateMonth: month });
      fetchStatus(month);
    } catch (e) {
      alert(e.response?.data?.message || '수신확인 실패');
    }
    setConfirmingId(null);
  };

  const handleCancel = async (clientId, clientName) => {
    if (!window.confirm(`${clientName} — 수신확인을 취소하시겠습니까?`)) return;
    setConfirmingId(clientId);
    try {
      await axios.post('/api/tax-invoice/manual-cancel', { clientId, calculateMonth: month });
      fetchStatus(month);
    } catch (e) {
      alert(e.response?.data?.message || '취소 실패');
    }
    setConfirmingId(null);
  };

  const handleAutoCarryover = async () => {
    const notIssuedCount = (data?.notIssued || []).filter(r => !r.nextAmount).length;
    if (notIssuedCount === 0) return alert('이월 처리할 미확인 건이 없습니다.');
    if (!window.confirm(`미확인 사업자 ${notIssuedCount}건을 자동 이월 처리합니다.\n계속하시겠습니까?`)) return;
    setCarryingOver(true);
    try {
      const { data: res } = await axios.post('/api/tax-invoice/auto-carryover', { calculateMonth: month });
      alert(`자동이월 완료: ${res.carryover}건 이월, ${res.skipped}건 스킵 (확인완료)`);
      fetchStatus(month);
    } catch (e) {
      alert(e.response?.data?.message || '자동이월 실패');
    }
    setCarryingOver(false);
  };

  const handleRestorePayment = async (clientId, clientName) => {
    if (!window.confirm(`${clientName}의 이월을 취소하고 지급 복원하시겠습니까?`)) return;
    try {
      await axios.post('/api/tax-invoice/restore-payment', { clientId, calculateMonth: month });
      alert('지급 복원 완료');
      fetchStatus(month);
    } catch (e) {
      alert(e.response?.data?.message || '복원 실패');
    }
  };

  const handleMonthChange = (dir) => {
    const idx = monthOptions.findIndex(o => o.value === month);
    const next = idx + dir;
    if (next >= 0 && next < monthOptions.length) setMonth(monthOptions[next].value);
  };

  const issued = data?.issued || [];
  const notIssued = data?.notIssued || [];
  const lateIssued = data?.lateIssued || [];

  return (
    <div>
      <div className="flex mb-8 justify-center items-center relative">
        <div className="text-xl sm:text-2xl font-bold text-white tracking-tight">세금계산서 현황</div>
      </div>

      {/* 월 선택 */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <button onClick={() => handleMonthChange(1)} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 transition-colors">
            <ChevronLeft size={16} />
          </button>
          <select
            value={month}
            onChange={e => setMonth(e.target.value)}
            className="px-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-white outline-none focus:ring-2 focus:ring-sky-500/30">
            {monthOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <button onClick={() => handleMonthChange(-1)} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 transition-colors">
            <ChevronRight size={16} />
          </button>
        </div>
        {data?.lastCheckedAt && (
          <span className="text-xs text-slate-500">마지막 확인: {fmtDate(data.lastCheckedAt)}</span>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-sky-400" />
        </div>
      ) : data ? (
        <>
          {/* 요약 카드 */}
          <div className={`grid gap-3 mb-6 ${lateIssued.length > 0 ? 'grid-cols-3' : 'grid-cols-2'}`}>
            <div
              onClick={() => setTab('issued')}
              className={`bg-[#182334] border rounded-2xl p-5 cursor-pointer transition-colors ${
                tab === 'issued' ? 'border-emerald-500/40' : 'border-white/[0.08] hover:border-white/20'
              }`}>
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 size={15} className="text-emerald-400" />
                <span className="text-xs text-slate-400">수신확인 완료</span>
              </div>
              <div className="text-3xl font-bold text-emerald-400">{issued.length}<span className="text-base text-slate-500 ml-1.5">명</span></div>
            </div>
            <div
              onClick={() => setTab('notIssued')}
              className={`bg-[#182334] border rounded-2xl p-5 cursor-pointer transition-colors ${
                tab === 'notIssued' ? 'border-rose-500/40' : 'border-white/[0.08] hover:border-white/20'
              }`}>
              <div className="flex items-center gap-2 mb-3">
                <AlertCircle size={15} className="text-rose-400" />
                <span className="text-xs text-slate-400">미확인</span>
              </div>
              <div className="text-3xl font-bold text-rose-400">{notIssued.length}<span className="text-base text-slate-500 ml-1.5">명</span></div>
              <div className="text-sm text-slate-500 mt-1">{fmt(data.notIssuedTotal)}원</div>
            </div>
            {lateIssued.length > 0 && (
              <div
                onClick={() => setTab('lateIssued')}
                className={`bg-[#182334] border rounded-2xl p-5 cursor-pointer transition-colors ${
                  tab === 'lateIssued' ? 'border-amber-500/40' : 'border-white/[0.08] hover:border-white/20'
                }`}>
                <div className="flex items-center gap-2 mb-3">
                  <Clock size={15} className="text-amber-400" />
                  <span className="text-xs text-slate-400">지연 확인 (이월 후)</span>
                </div>
                <div className="text-3xl font-bold text-amber-400">{lateIssued.length}<span className="text-base text-slate-500 ml-1.5">명</span></div>
              </div>
            )}
          </div>

          {/* 테이블 */}
          <div className="bg-[#182334] border border-white/[0.08] rounded-2xl overflow-hidden">
            {tab === 'notIssued' && notIssued.some(r => !r.nextAmount) && (
              <div className="px-4 py-3 border-b border-white/[0.06] flex items-center justify-end">
                <button
                  onClick={handleAutoCarryover}
                  disabled={carryingOver}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-rose-500/20 hover:bg-rose-500/30 text-rose-400 transition-colors disabled:opacity-50">
                  {carryingOver ? <Loader2 size={12} className="animate-spin" /> : <AlertCircle size={12} />}
                  {carryingOver ? '처리 중...' : '미확인 전체 이월처리'}
                </button>
              </div>
            )}

            <div className="overflow-x-auto">
              {tab === 'issued' ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/[0.06]">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">권리사</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">사업자번호</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">수령액</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">확인일시</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">취소</th>
                    </tr>
                  </thead>
                  <tbody>
                    {issued.length === 0 ? (
                      <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">수신확인된 권리사가 없습니다.</td></tr>
                    ) : issued.map(row => (
                      <tr key={row.clientId} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
                        <td className="px-4 py-3 font-medium text-white">{row.clientName}</td>
                        <td className="px-4 py-3 text-slate-400 text-xs">{row.businessNumber}</td>
                        <td className="px-4 py-3 text-right text-emerald-400 font-bold">{fmt(row.receiveAmount)}</td>
                        <td className="px-4 py-3 text-right text-slate-400 text-xs">
                          {row.invoices?.[0]?.checkedAt ? fmtDate(row.invoices[0].checkedAt) : '-'}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => handleCancel(row.clientId, row.clientName)}
                            disabled={confirmingId === row.clientId}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-slate-500/20 hover:bg-slate-500/30 text-slate-400 transition-colors disabled:opacity-50">
                            {confirmingId === row.clientId ? <Loader2 size={11} className="animate-spin" /> : <XCircle size={11} />}
                            취소
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : tab === 'lateIssued' ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/[0.06]">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">권리사</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">사업자번호</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">수령액</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">이월액</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">복원</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lateIssued.length === 0 ? (
                      <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">지연 확인 건이 없습니다.</td></tr>
                    ) : lateIssued.map(row => (
                      <tr key={row.clientId} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
                        <td className="px-4 py-3 font-medium text-white">{row.clientName}</td>
                        <td className="px-4 py-3 text-slate-400 text-xs">{row.businessNumber}</td>
                        <td className="px-4 py-3 text-right text-slate-300">{fmt(row.receiveAmount)}</td>
                        <td className="px-4 py-3 text-right text-rose-400">{fmt(row.nextAmount)}</td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => handleRestorePayment(row.clientId, row.clientName)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 transition-colors">
                            <Undo2 size={11} />
                            지급복원
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/[0.06]">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">권리사</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">사업자번호</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-rose-400 uppercase tracking-wide">수령액</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">상태</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">수신확인</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notIssued.length === 0 ? (
                      <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">미확인 권리사가 없습니다.</td></tr>
                    ) : notIssued.map(row => (
                      <tr key={row.clientId} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
                        <td className="px-4 py-3 font-medium text-white">{row.clientName}</td>
                        <td className="px-4 py-3 text-slate-400 text-xs">{row.businessNumber}</td>
                        <td className="px-4 py-3 text-right font-bold text-rose-400">{fmt(row.receiveAmount)}</td>
                        <td className="px-4 py-3 text-right">
                          {row.nextAmount > 0
                            ? <span className="text-xs px-2 py-0.5 rounded-full bg-slate-500/20 text-slate-400">이월완료</span>
                            : <span className="text-xs px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-400">대기중</span>
                          }
                        </td>
                        <td className="px-4 py-3 text-center">
                          {!row.nextAmount && (
                            <button
                              onClick={() => handleConfirm(row.clientId, row.clientName)}
                              disabled={confirmingId === row.clientId}
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 transition-colors disabled:opacity-50">
                              {confirmingId === row.clientId ? <Loader2 size={11} className="animate-spin" /> : <CheckCheck size={11} />}
                              수신확인
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center justify-center h-48 text-slate-500">
          <FileText size={32} className="mb-3 opacity-30" />
          <p className="text-sm">세금계산서 데이터가 없습니다.</p>
        </div>
      )}
    </div>
  );
}
